import { useState } from 'react'
import { toast } from 'sonner'
import { copyToClipboard } from '@/lib/clipboard-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { PortalAttributionModeEnum } from '@/gen/cortado/v1/settings_pb'

type PendingVariant = 'recipient' | 'generic' | null

interface PortalLinkResponse {
  url: string
}

/**
 * Shared copy/generate/toast flow for "Copy Portal Link" buttons.
 *
 * `generateLink(attributionMode)` should call the inquiry/invoice-specific
 * portal-link RPC with explicit recipient or anonymous intent.
 *
 * If `onError` is omitted, a generic error toast is shown; pass it to
 * fully customize error handling (it replaces the default toast).
 */
export function usePortalLinkCopy(
  generateLink: (attributionMode: PortalAttributionModeEnum) => Promise<PortalLinkResponse>,
  onError?: (error: unknown) => void
) {
  const [pending, setPending] = useState<PendingVariant>(null)

  const copy = async (attributionMode: PortalAttributionModeEnum) => {
    setPending(
      attributionMode === PortalAttributionModeEnum.PORTAL_ATTRIBUTION_MODE_RECIPIENT
        ? 'recipient'
        : 'generic'
    )
    try {
      const response = await generateLink(attributionMode)
      copyToClipboard(response.url)
      toast.success('Client portal link copied')
    } catch (error) {
      if (onError) {
        onError(error)
      } else {
        toast.error('Failed to generate portal link', {
          description: getErrorDescription(error),
        })
      }
    } finally {
      setPending(null)
    }
  }

  return {
    copyRecipientLink: () => copy(PortalAttributionModeEnum.PORTAL_ATTRIBUTION_MODE_RECIPIENT),
    copyGenericLink: () => copy(PortalAttributionModeEnum.PORTAL_ATTRIBUTION_MODE_ANONYMOUS),
    isCopyingRecipientLink: pending === 'recipient',
    isCopyingGenericLink: pending === 'generic',
    isCopying: pending !== null,
  }
}
